import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStorefrontProducts } from './useStorefrontProducts';

// Scroll-triggered "The Sale Is On" modal. Fires once per browser session the
// first time the On Sale section scrolls into view. Purely an app-side sale
// announcement (no code, no email, no OffloadHost promo wiring) — it drives
// shoppers to /product-catalog?filter=sale. The soap image + max-discount are
// pulled from the real catalog so the card always reflects the live sale.
const SEEN_KEY = 'bl_sale_promo_seen';

const SalePromoModal = ({ targetId = 'onsale-section' }) => {
  const navigate = useNavigate();
  const { products } = useStorefrontProducts();
  const [open, setOpen] = useState(false);
  const armed = useRef(false);

  const saleItems = (products || []).filter((p) => p?.sale_price_cents != null);
  const image = (saleItems[0] || (products || [])[0])?.image_url;
  const maxOff = saleItems.reduce((m, p) => {
    const off = p?.price_cents ? Math.floor(((p.price_cents - p.sale_price_cents) / p.price_cents) * 100) : 0;
    return off > m ? off : m;
  }, 0);

  useEffect(() => {
    if (armed.current || !saleItems.length) return;
    try { if (sessionStorage.getItem(SEEN_KEY)) return; } catch (_) { /* private mode */ }
    const el = document.getElementById(targetId);
    if (!el) return;
    armed.current = true;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setOpen(true);
        try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* ignore */ }
        io.disconnect();
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [saleItems.length, targetId]);

  const close = () => setOpen(false);
  const shopSale = () => { close(); navigate('/product-catalog?filter=sale'); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
          <motion.div
            role="dialog" aria-modal="true" aria-label="Sale announcement"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-2xl w-full max-w-lg flex flex-col sm:flex-row">
            <button
              type="button" onClick={close} aria-label="Close"
              className="absolute top-3 right-3 z-10 bg-background/80 backdrop-blur rounded-full p-1.5 hover:bg-background transition-colors">
              <Icon name="X" size={18} className="text-foreground" />
            </button>

            {image && (
              <div className="sm:w-1/2 aspect-[4/5] sm:aspect-auto relative overflow-hidden bg-muted">
                <img src={image} alt="Handcrafted soap on sale" className="w-full h-full object-cover" />
                {maxOff > 0 && (
                  <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full shadow">
                    Up to {maxOff}% off
                  </span>
                )}
              </div>
            )}

            <div className="sm:w-1/2 p-6 sm:p-8 flex flex-col justify-center text-center sm:text-left">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">Limited time</p>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-2 leading-tight">The Sale Is On</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Save on our handcrafted, small-batch soaps &amp; body care — while they last.
              </p>
              <Button onClick={shopSale} className="w-full sm:w-auto">
                Shop the Sale
                <Icon name="ArrowRight" size={16} className="ml-2" />
              </Button>
              <button
                type="button" onClick={close}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors">
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SalePromoModal;
